import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const MailBoxWidget = ({
  emails,
  loading,
  onOpenInbox,
  gmailConnected,
  onConnectGmail,
  activeProvider,
  setActiveProvider,
  gmailProfilePic,
}) => {
  const isAnyConnected = gmailConnected;

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #EBEBEB",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        height: "400px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: "16px 20px 12px",
          borderBottom: "1px solid #F3F4F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: "14px",
            fontWeight: 700,
            color: "#1A1D1F",
            letterSpacing: "-0.01em",
          }}
        >
          Email Box
        </span>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={gmailConnected ? "Gmail Connected" : "Connect Gmail"}>
            {gmailConnected && gmailProfilePic ? (
              <img 
                src={gmailProfilePic} 
                alt="Profile" 
                style={{ width: '24px', height: '24px', borderRadius: '50%', border: '1px solid #e5e7eb', objectFit: 'cover' }} 
                referrerPolicy="no-referrer" 
              />
            ) : (
              <IconButton
                size="small"
                onClick={gmailConnected ? null : onConnectGmail}
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: gmailConnected ? "transparent" : "#FEF2F2",
                  color: gmailConnected ? "#1E8E3E" : "#EA4335",
                  "&:hover": { bgcolor: gmailConnected ? "transparent" : "#FEE2E2" },
                }}
              >
                <GoogleIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Tooltip>

          {isAnyConnected && (
            <button
              onClick={onOpenInbox}
              style={{
                background: "transparent",
                border: "none",
                color: "#6C5DD3",
                fontWeight: 600,
                fontSize: "12px",
                cursor: "pointer",
                padding: "2px 0",
                letterSpacing: "-0.01em",
              }}
            >
              Open Inbox →
            </button>
          )}
        </Stack>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#E5E7EB transparent",
        }}
      >
        {!gmailConnected ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              padding: "20px",
            }}
          >
            <span
              style={{
                fontSize: "13px",
                color: "#9CA3AF",
                textAlign: "center",
              }}
            >
              Connect your email to see recent messages
            </span>
          </div>
        ) : loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={22} sx={{ color: "#6C5DD3" }} />
          </div>
        ) : (
          <div style={{ padding: "6px 0" }}>
            {emails.length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  color: "#9CA3AF",
                  fontSize: "13px",
                  marginTop: "40px",
                }}
              >
                No messages found.
              </p>
            ) : (
              emails.map((email, index) => {
                const extractName = (str) => {
                  if (!str) return "Unknown";
                  if (typeof str === "object")
                    return str.name || str.address || "Unknown";
                  const match = str.match(/^(.*?)(?:\s*<.*>)?$/);
                  return match ? match[1].replace(/"/g, "").trim() : str;
                };
                const senderName = extractName(email.from);
                const initials =
                  senderName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase() || "E";
                const colors = ["#9757D7", "#377DFF", "#45E364"];
                const avatarColor = colors[index % colors.length];

                return (
                  <div
                    key={email.id || index}
                    onClick={onOpenInbox}
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "flex-start",
                      padding: "10px 20px",
                      borderBottom: "1px solid #F9FAFB",
                      cursor: "pointer",
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#FAFAFA")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <Avatar
                      sx={{
                        bgcolor: avatarColor,
                        width: 36,
                        height: 36,
                        fontSize: "13px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "2px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#1A1D1F",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "140px",
                          }}
                        >
                          {senderName}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#9CA3AF",
                            flexShrink: 0,
                          }}
                        >
                          {email.timeAgo || "Now"}
                        </span>
                      </div>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6F767E",
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 500,
                        }}
                      >
                        {email.subject || "No subject"}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MailBoxWidget;
